import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchBookingsForToday } from '../lib/bookings';
import {
  fetchQueue,
  subscribeToQueueChanges,
} from '../api/queue';
import { formatError } from '../lib/errors';
import { venueDayBounds, formatVenueDate } from '../lib/time';
import type { BookingRow, LifecycleStatus, QueueEntry } from '../types';
import { BookingCard } from '../components/BookingCard';
import { QueueRow } from '../components/QueueRow';
import { QueueUndoButton } from '../components/QueueUndoButton';
import { QueueAddPicker } from '../components/QueueAddPicker';
import { useLocationFilter } from '../lib/location-context';
import { useRoomsAtLocation } from '../lib/room-counts';

// Realtime carries the bulk of the freshness story; the poll is a 60s safety
// net for the gap between websocket reconnects.
const SAFETY_REFRESH_MS = 60_000;

export function TodayScreen() {
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queueNotice, setQueueNotice] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [addPickerOpen, setAddPickerOpen] = useState(false);

  const bounds = useMemo(() => venueDayBounds(), []);
  const { locationId } = useLocationFilter();
  const roomIds = useRoomsAtLocation(locationId);

  const loadBookings = useCallback(async () => {
    try {
      const rows = await fetchBookingsForToday(bounds, { roomIds });
      setBookings(rows);
      setLastRefresh(new Date());
      setError(null);
    } catch (e) {
      setError(formatError(e));
    }
  }, [bounds, roomIds]);

  const loadQueue = useCallback(async () => {
    if (!locationId) {
      setQueue([]);
      return;
    }
    try {
      const entries = await fetchQueue(locationId);
      setQueue(entries);
    } catch (e) {
      // Queue load errors don't tear down the screen — the pending list still
      // renders. Surface them as a thin notice so the operator notices.
      setQueueNotice(`Wachtrij kon niet laden: ${formatError(e)}`);
    }
  }, [locationId]);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadBookings(), loadQueue()]);
    setLoading(false);
  }, [loadBookings, loadQueue]);

  useEffect(() => {
    void reloadAll();
    const t = setInterval(() => void reloadAll(), SAFETY_REFRESH_MS);
    return () => clearInterval(t);
  }, [reloadAll]);

  // Live updates from booking.queue_entries scoped to the current location.
  // Debounced: a per-person enqueue inserts N rows in one transaction → N
  // postgres_changes events → without the timer this would fire N parallel
  // edge-function fetches and trip ERR_INSUFFICIENT_RESOURCES in the browser.
  useEffect(() => {
    if (!locationId) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const unsub = subscribeToQueueChanges(locationId, () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { void loadQueue(); }, 300);
    });
    return () => {
      if (t) clearTimeout(t);
      unsub();
    };
  }, [locationId, loadQueue]);

  const onLifecycleChange = useCallback((id: string, next: LifecycleStatus) => {
    setBookings((prev) =>
      prev.map((b) => (b.id === id ? { ...b, lifecycle_status: next } : b)),
    );
    // Lifecycle 'arrived' triggers server-side enqueue. The realtime
    // subscription will pick up the new queue row within ~1s.
  }, []);

  // Departures currently in the queue should not also appear in the pending
  // section — the queue is their authoritative home there. BookingRow.id
  // is the departure_id post multi-experience cart, matching QueueEntry.
  const queuedDepartureIds = useMemo(
    () => new Set(queue.map((q) => q.departure_id)),
    [queue],
  );
  const pendingBookings = useMemo(
    () => bookings.filter((b) => !queuedDepartureIds.has(b.id)),
    [bookings, queuedDepartureIds],
  );

  // Subset shown to the QueueAddPicker — already checked in but not in the
  // queue (i.e. removed/started rows OR a checked-in row that never auto-
  // enqueued for some reason). 'confirmed' bookings stay out per locked
  // precondition; operator must use the 'Check in' button for those.
  const arrivedNotInQueue = useMemo(
    () => pendingBookings.filter((b) => b.lifecycle_status === 'arrived'),
    [pendingBookings],
  );

  const grouped = useMemo(() => {
    const m = new Map<string, BookingRow[]>();
    for (const b of pendingBookings) {
      const key = b.booking_slots[0]?.slot_start ?? 'unknown';
      const arr = m.get(key) ?? [];
      arr.push(b);
      m.set(key, arr);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [pendingBookings]);

  const showQueueSection = locationId !== null;
  const parkedCount = useMemo(() => queue.filter((q) => q.parked).length, [queue]);

  return (
    <div className="screen today-screen">
      <div className="screen-header">
        <h1>{formatVenueDate(bounds.startUtc)}</h1>
        <div className="screen-meta">
          <span>{bookings.length} boekingen</span>
          {showQueueSection ? (
            <span className="muted">
              · {queue.length} in wachtrij
              {parkedCount > 0 ? ` (${parkedCount} even weg)` : ''}
            </span>
          ) : null}
          {lastRefresh ? (
            <span className="muted">
              · ververst {lastRefresh.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          ) : null}
          <button className="btn-link" onClick={() => void reloadAll()}>Ververs</button>
        </div>
      </div>

      {loading ? <div className="muted">Laden…</div> : null}
      {error ? <div className="error-card">{error}</div> : null}

      {showQueueSection ? (
        <section className="queue-section">
          <div className="queue-section-header">
            <h2>Wachtrij</h2>
            <div className="queue-section-header-actions">
              <button
                type="button"
                className="btn-secondary queue-add-btn"
                onClick={() => setAddPickerOpen((v) => !v)}
                disabled={arrivedNotInQueue.length === 0}
                title="Voeg een aangekomen boeking handmatig toe aan de wachtrij."
              >
                + Voeg toe
              </button>
              <QueueUndoButton
                locationId={locationId!}
                onError={setQueueNotice}
                onMutated={() => void loadQueue()}
              />
            </div>
          </div>
          {addPickerOpen ? (
            <QueueAddPicker
              arrivedNotInQueue={arrivedNotInQueue}
              onClose={() => setAddPickerOpen(false)}
              onAdded={() => void loadQueue()}
            />
          ) : null}
          {queueNotice ? (
            <div className="queue-notice" role="status" onClick={() => setQueueNotice(null)}>
              {queueNotice} <span className="muted">(tik om te sluiten)</span>
            </div>
          ) : null}
          {queue.length === 0 ? (
            <div className="muted queue-empty">Geen klanten in de wachtrij.</div>
          ) : (
            <div className="queue-list">
              {queue.map((q) => (
                <QueueRow
                  key={q.queue_entry_id}
                  entry={q}
                  total={queue.length}
                  onError={setQueueNotice}
                  onMutated={() => void loadQueue()}
                />
              ))}
            </div>
          )}
        </section>
      ) : (
        <div className="muted location-required-notice">
          Selecteer een locatie om de wachtrij te zien.
        </div>
      )}

      {!loading && !error && pendingBookings.length === 0 ? (
        <div className="muted">Geen openstaande boekingen vandaag.</div>
      ) : null}

      {grouped.map(([slotKey, group]) => (
        <section key={slotKey} className="time-group">
          <h2 className="time-group-header">
            {new Date(slotKey).toLocaleTimeString('nl-NL', {
              timeZone: 'Europe/Amsterdam',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </h2>
          <div className="booking-list">
            {group.map((b) => (
              <BookingCard
                key={b.id}
                booking={b}
                onLifecycleChange={onLifecycleChange}
                onRequeued={() => void loadQueue()}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
